import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import conversationService from '../services/conversationService';
import useNotification from '../hooks/useNotification';
import Notification from '../components/Notification';

/**
 * The human window onto autonomous conversation.
 *
 * Orion answers prospects by itself. Somebody has to be able to read what it
 * said, see why it stayed quiet, and step in - otherwise it is a system nobody
 * can supervise. Four ways to intervene: send the drafted reply (edited or
 * not), write your own, pause autopilot on this one thread, or discard a draft
 * you disagree with.
 */

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-family: var(--font-heading);
  font-size: var(--text-3xl);
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1.05rem;
  margin: 0.75rem 0 1.5rem;
  line-height: 1.6;
  max-width: 760px;
`;

const Tabs = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Tab = styled.button`
  background: ${(p) => (p.$active ? 'rgba(96,165,250,0.14)' : 'transparent')};
  border: 1px solid ${(p) => (p.$active ? 'rgba(96,165,250,0.5)' : 'var(--border-color)')};
  color: ${(p) => (p.$active ? '#60a5fa' : 'var(--text-secondary)')};
  border-radius: var(--radius-md);
  padding: 0.55rem 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;

  &:hover { color: var(--text-primary); }
`;

const Count = styled.span`
  display: inline-block;
  margin-left: 0.45rem;
  padding: 0.05rem 0.45rem;
  border-radius: 999px;
  background: rgba(251,191,36,0.18);
  color: #fbbf24;
  font-size: 0.72rem;
`;

const Split = styled.div`
  display: grid;
  grid-template-columns: minmax(280px, 380px) 1fr;
  gap: 1.25rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background: var(--gradient-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  overflow: hidden;
`;

const ThreadRow = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  background: ${(p) => (p.$active ? 'rgba(96,165,250,0.08)' : 'transparent')};
  border: none;
  border-bottom: 1px solid rgba(255,255,255,0.05);
  border-left: 3px solid ${(p) => (p.$attention ? '#fbbf24' : 'transparent')};
  padding: 0.9rem 1rem;
  cursor: pointer;
  color: var(--text-primary);

  &:hover { background: rgba(255,255,255,0.04); }
`;

const Who = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
`;

const Muted = styled.div`
  color: var(--text-muted);
  font-size: 0.8rem;
  margin-top: 0.2rem;
  line-height: 1.4;
`;

const Chip = styled.span`
  display: inline-block;
  padding: 0.16rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  border: 1px solid ${(p) => p.$border};
  margin-right: 0.35rem;
  margin-top: 0.35rem;
`;

const ThreadHeader = styled.div`
  padding: 1.25rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Messages = styled.div`
  padding: 1.25rem;
  max-height: 46vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const Bubble = styled.div`
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.8rem 0.95rem;
  background: ${(p) => (p.$inbound ? 'rgba(255,255,255,0.04)' : 'rgba(96,165,250,0.07)')};
  align-self: ${(p) => (p.$inbound ? 'flex-start' : 'flex-end')};
  max-width: 82%;
  white-space: pre-wrap;
  line-height: 1.55;
  font-size: 0.9rem;
  color: var(--text-primary);
`;

const Who2 = styled.div`
  font-size: 0.72rem;
  color: var(--text-muted);
  margin-bottom: 0.35rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const ActionArea = styled.div`
  padding: 1.25rem;
  border-top: 1px solid var(--border-color);
`;

const DraftBox = styled.div`
  border: 1px solid rgba(251,191,36,0.35);
  background: rgba(251,191,36,0.06);
  border-radius: var(--radius-md);
  padding: 0.9rem;
  margin-bottom: 1rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  background: rgba(22, 26, 43,0.8);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.75rem;
  color: var(--text-primary);
  font-family: var(--font-primary);
  font-size: 0.9rem;
  line-height: 1.55;
  resize: vertical;

  &:focus { outline: none; border-color: var(--accent-cyan); }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 0.75rem;
`;

const Button = styled.button`
  border-radius: var(--radius-md);
  padding: 0.6rem 1.1rem;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;

  background: ${(p) => (p.$variant === 'primary' ? 'var(--accent-cyan)'
    : p.$variant === 'danger' ? 'rgba(248,113,113,0.14)' : 'transparent')};
  color: ${(p) => (p.$variant === 'primary' ? 'var(--primary-bg)'
    : p.$variant === 'danger' ? '#f87171' : 'var(--text-secondary)')};
  border-color: ${(p) => (p.$variant === 'danger' ? 'rgba(248,113,113,0.4)'
    : p.$variant === 'primary' ? 'transparent' : 'var(--border-color)')};

  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &:not(:disabled):hover { filter: brightness(1.1); }
`;

const Empty = styled.div`
  padding: 3.5rem 2rem;
  text-align: center;
  color: var(--text-secondary);
  line-height: 1.7;
`;

const FILTERS = [
  { key: 'all', label: 'All threads' },
  { key: 'needs_attention', label: 'Needs you' },
  { key: 'awaiting_approval', label: 'Awaiting approval' },
  { key: 'paused', label: 'Autopilot paused' }
];

const Conversations = () => {
  const [threads, setThreads] = useState([]);
  const [needsAttention, setNeedsAttention] = useState(0);
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [draft, setDraft] = useState('');
  const [manual, setManual] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const { notifications, showSuccess, showError, removeNotification } = useNotification();

  const loadList = useCallback(async () => {
    try {
      setLoading(true);
      const data = await conversationService.list({ filter: filter === 'all' ? undefined : filter });
      setThreads(data.conversations || []);
      setNeedsAttention(data.needsAttention || 0);
    } catch (err) {
      showError('Could not load conversations');
    } finally {
      setLoading(false);
    }
  }, [filter, showError]);

  useEffect(() => { loadList(); }, [loadList]);

  // Threads change while a person is looking at them, because replies keep
  // arriving. Refreshing quietly keeps the queue honest without stealing focus.
  useEffect(() => {
    const timer = setInterval(loadList, 20000);
    return () => clearInterval(timer);
  }, [loadList]);

  const openThread = useCallback(async (id) => {
    setSelectedId(id);
    setManual('');
    try {
      const data = await conversationService.get(id);
      setThread(data);
      setDraft(data.pendingReply ? data.pendingReply.text : '');
    } catch (err) {
      showError('Could not open that conversation');
    }
  }, [showError]);

  const act = async (fn, successMessage) => {
    try {
      setBusy(true);
      await fn();
      if (successMessage) showSuccess(successMessage);
      await loadList();
      if (selectedId) await openThread(selectedId);
    } catch (err) {
      showError(err?.response?.data?.message || 'That did not work');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container>
      <Notification notifications={notifications} removeNotification={removeNotification} />

      <Title>Conversations</Title>
      <Subtitle>
        Every exchange Orion is having, and what it decided. Send a drafted reply, write
        your own, or pause autopilot on a single thread without stopping the rest.
      </Subtitle>

      <Tabs>
        {FILTERS.map((f) => (
          <Tab key={f.key} $active={filter === f.key} onClick={() => { setFilter(f.key); setSelectedId(null); setThread(null); }}>
            {f.label}
            {f.key === 'needs_attention' && needsAttention > 0 && <Count>{needsAttention}</Count>}
          </Tab>
        ))}
      </Tabs>

      <Split>
        <Panel>
          {loading && threads.length === 0 ? (
            <Empty>Loading...</Empty>
          ) : threads.length === 0 ? (
            <Empty>
              {filter === 'all'
                ? 'No conversations yet. They appear once a prospect replies.'
                : 'Nothing in this view.'}
            </Empty>
          ) : threads.map((t) => (
            <ThreadRow
              key={t.id}
              $active={t.id === selectedId}
              $attention={Boolean(t.attention)}
              onClick={() => openThread(t.id)}
            >
              <Who>{(t.prospect && t.prospect.name) || 'Unknown'}</Who>
              <Muted>{(t.prospect && t.prospect.company) || t.subject || ''}</Muted>
              {t.lastMessage && (
                <Muted>
                  {t.lastMessage.direction === 'inbound' ? 'They: ' : 'Orion: '}
                  {t.lastMessage.text.slice(0, 90)}
                </Muted>
              )}
              <div>
                {t.attention && (
                  <Chip $bg="rgba(251,191,36,0.14)" $fg="#fbbf24" $border="rgba(251,191,36,0.4)">needs you</Chip>
                )}
                {t.pendingReply && (
                  <Chip $bg="rgba(96,165,250,0.14)" $fg="#60a5fa" $border="rgba(96,165,250,0.4)">draft ready</Chip>
                )}
                {t.autoReplyPaused && (
                  <Chip $bg="rgba(148,163,184,0.14)" $fg="#94a3b8" $border="rgba(148,163,184,0.4)">paused</Chip>
                )}
              </div>
            </ThreadRow>
          ))}
        </Panel>

        <Panel>
          {!thread ? (
            <Empty>Pick a conversation to read it.</Empty>
          ) : (
            <>
              <ThreadHeader>
                <div>
                  <Who>{(thread.prospect && thread.prospect.name) || 'Unknown'}</Who>
                  <Muted>
                    {[thread.prospect && thread.prospect.title, thread.prospect && thread.prospect.company]
                      .filter(Boolean).join(' · ')}
                  </Muted>
                  <Muted>{thread.prospect && thread.prospect.email}</Muted>
                  {thread.classification && (
                    <Muted>
                      Last message read as <strong>{thread.classification.kind}</strong>
                      {thread.classification.reason ? ` — ${thread.classification.reason}` : ''}
                    </Muted>
                  )}
                  {thread.attention && (
                    <Muted style={{ color: '#fbbf24' }}>Needs you: {thread.attention.reason}</Muted>
                  )}
                  {thread.lastError && (
                    <Muted style={{ color: '#f87171' }}>Last error: {thread.lastError}</Muted>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Muted>{thread.autoReplyTurns} automatic repl{thread.autoReplyTurns === 1 ? 'y' : 'ies'} sent</Muted>
                  <Button
                    style={{ marginTop: '0.5rem' }}
                    disabled={busy}
                    onClick={() => act(
                      () => conversationService.setAutopilot(thread.id, !thread.autoReplyPaused),
                      thread.autoReplyPaused ? 'Autopilot resumed on this thread' : 'Autopilot paused on this thread'
                    )}
                  >
                    {thread.autoReplyPaused ? 'Resume autopilot' : 'Pause autopilot'}
                  </Button>
                </div>
              </ThreadHeader>

              <Messages>
                {(thread.messages || []).length === 0 ? (
                  <Muted>Nothing exchanged yet.</Muted>
                ) : thread.messages.map((m, i) => (
                  <Bubble key={i} $inbound={m.direction === 'inbound'}>
                    <Who2>{m.direction === 'inbound' ? 'Prospect' : 'Orion'}</Who2>
                    {m.text}
                  </Bubble>
                ))}
              </Messages>

              <ActionArea>
                {thread.pendingReply && (
                  <DraftBox>
                    <Muted style={{ marginBottom: '0.5rem' }}>
                      Orion drafted this and is waiting for you. Edit it if you like — what you
                      send is what goes.
                    </Muted>
                    <TextArea value={draft} onChange={(e) => setDraft(e.target.value)} />
                    <Row>
                      <Button
                        $variant="primary"
                        disabled={busy || !draft.trim()}
                        onClick={() => act(
                          () => conversationService.approve(thread.id, { text: draft }),
                          'Reply sent'
                        )}
                      >
                        Send this reply
                      </Button>
                      <Button
                        $variant="danger"
                        disabled={busy}
                        onClick={() => act(() => conversationService.discard(thread.id), 'Draft discarded')}
                      >
                        Discard draft
                      </Button>
                    </Row>
                  </DraftBox>
                )}

                <Muted style={{ marginBottom: '0.4rem' }}>Write your own reply</Muted>
                <TextArea
                  placeholder="Taking over pauses autopilot on this thread."
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                />
                <Row>
                  <Button
                    $variant="primary"
                    disabled={busy || !manual.trim()}
                    onClick={() => act(
                      () => conversationService.reply(thread.id, { text: manual }),
                      'Sent. Autopilot is paused on this thread.'
                    )}
                  >
                    Send
                  </Button>
                  {thread.attention && (
                    <Button
                      disabled={busy}
                      onClick={() => act(() => conversationService.resolve(thread.id), 'Marked as handled')}
                    >
                      Mark handled
                    </Button>
                  )}
                </Row>
              </ActionArea>
            </>
          )}
        </Panel>
      </Split>
    </Container>
  );
};

export default Conversations;
